import React, { useState } from 'react';
import { Phone, ArrowRight } from 'react-feather';
import Button from '../ui/Button';
import { trackEvent, trackConversion, generateConversionEventId } from '../../services/trackingService';
import { submitContactForm } from '../../services/apiService';
import { useUtm } from '../../hooks/useUtm';
import { useData } from '../../context/DataContext';
import { useTranslation } from 'react-i18next';

/**
 * A practice-area page's own real slug -> the exact LeadDocket case-type
 * name ContactPage.tsx's personalInjuryCaseTypes already uses. Kept as its
 * own small map (not imported from ContactPage) since that file's list is
 * a component-local constant, not a shared export -- duplicating 9 short
 * strings here is simpler than restructuring that file for one new caller.
 * Anything not personal-injury (bankruptcy, estate-planning, wills-and-
 * trusts) or not in this list falls back to "Other", the same real,
 * already-valid catch-all value the full contact form itself uses.
 */
const SLUG_TO_CASE_TYPE: Record<string, string> = {
    'car-accidents': 'Motor Vehicle Accident',
    'trucking-accidents': 'Trucking Accident',
    'motorcycle-accidents': 'Motorcycle Accident',
    'premises-liability': 'Slip And Fall / Premises Liability',
    'pedestrian-accidents': 'Pedestrian / Crosswalk Accident',
    'wrongful-death': 'Wrongful Death',
    'dog-bites': 'Dog / Animal Bite',
    'rideshare-accidents': 'Rideshare',
    'bicycle-accidents': 'eScooter / eBike / Bicycle Accident',
};

/**
 * "Can't talk right now?" fast-track lead capture -- 2026-09-19, client:
 * "lets do the quick call me back forms, but what about the call us
 * button i already have - instead of all me back shouldnt we just tell
 * them to just call us now?" Real answer, not a replacement for the call
 * CTA: the phone button stays the dominant, most-encouraged action (it's
 * rendered first, larger, and is the one real 24/7-immediate option) --
 * this form exists ONLY to catch the real, separate segment of visitors
 * who won't call (at work, in pain, browsing at night, phone-anxious) and
 * would otherwise leave with zero capture at all. It reuses the EXACT
 * same real pipeline ContactPage.tsx's full 3-step form already uses
 * (submitContactForm -> notifySlackForm.ts: instant Slack alert + Lead
 * Docket CRM + server-side Meta CAPI) -- a "callback in minutes" is a
 * real claim because that real-time alert already exists, not a promise
 * this form invents on its own.
 */
const QuickCallbackForm: React.FC<{ practiceAreaSlug?: string }> = ({ practiceAreaSlug }) => {
    const { t } = useTranslation('common');
    const { firmInfo } = useData();
    const { getUtmFields } = useUtm();

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const caseType = (practiceAreaSlug && SLUG_TO_CASE_TYPE[practiceAreaSlug]) || 'Other';

    const handleCallClick = () => {
        trackEvent('click_to_call', { location: 'quick_callback_form' });
        trackConversion('Contact', { content_name: 'click_to_call', location: 'quick_callback_form' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors: { name?: string; phone?: string } = {};
        if (!name.trim()) newErrors.name = t('quickCallbackForm.nameError');
        if (!phone.trim() || !/^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/.test(phone)) {
            newErrors.phone = t('quickCallbackForm.phoneError');
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setIsSubmitting(true);
        setStatus('idle');

        const utmFields = getUtmFields();
        const fbEventId = generateConversionEventId('Lead');
        // Same real payload shape notifySlackForm.ts already expects from
        // ContactPage.tsx -- email/incidentDate/incidentDescription are
        // genuinely optional there (used only for the Lead Docket summary
        // text and its "N/A" fallback), so omitting them here is honest,
        // not a workaround: this really is all the info a fast-track
        // callback request has.
        const submissionData = {
            name, phone, email: '', caseType,
            incidentDate: '', incidentDescription: `Quick callback request from ${practiceAreaSlug ? `/practice-areas/${practiceAreaSlug}` : 'the website'} -- no case details provided yet, call back to gather them.`,
            ...utmFields, fbEventId,
        };

        try {
            await submitContactForm(submissionData);
            setStatus('success');
            trackEvent('quick_callback_submit', { location: practiceAreaSlug || 'unknown' });
            trackConversion('Lead', { content_name: 'quick_callback_form', content_category: caseType }, fbEventId);
        } catch (error) {
            console.error('Quick callback form submission error:', error);
            setStatus('error');
            trackEvent('form_submission_error', { error: (error as Error).message, form: 'quick_callback' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass = (field: 'name' | 'phone') =>
        `w-full bg-white/5 border ${errors[field] ? 'border-red-500' : 'border-white/10'} rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-brand-gold`;

    return (
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/5 glass-effect p-8 md:p-10 shadow-card">
            {/* Primary path: call now -- deliberately the larger, first-rendered action */}
            <div className="text-center">
                <h3 className="text-2xl font-bold font-serif">{t('quickCallbackForm.callNowTitle')}</h3>
                <p className="mt-2 text-white/80">{t('quickCallbackForm.callNowSubtitle', { phone: firmInfo.displayPhone })}</p>
                <div className="mt-6">
                    {/* A real, deliberate anchor, not <Button href=.../> -- Button.tsx's
                        href branch doesn't forward onClick at all (confirmed by reading
                        it directly), which is exactly why every other real click-to-call
                        link in this codebase (Hero.tsx, Footer.tsx, Navbar.tsx) already
                        uses a raw <a> instead. Using Button here would have silently
                        dropped this tracking call. */}
                    <a
                        href={`tel:${firmInfo.telephone}`}
                        onClick={handleCallClick}
                        className="inline-flex items-center justify-center gap-2 rounded-xl font-semibold text-center gold-gradient-bg text-black px-8 py-4 text-lg shadow-lg shadow-brand-gold/20 hover:brightness-110 transition-colors duration-300 ease-in-out"
                    >
                        <Phone size={18} />
                        {t('quickCallbackForm.callNowButton', { phone: firmInfo.displayPhone })}
                    </a>
                </div>
            </div>

            <div className="my-8 flex items-center gap-4">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-xs uppercase tracking-wider text-white/40">{t('quickCallbackForm.dividerText')}</span>
                <div className="h-px flex-1 bg-white/10" />
            </div>

            {/* Secondary, low-friction path for visitors who won't call right now */}
            <div>
                <h4 className="text-lg font-semibold text-center">{t('quickCallbackForm.formTitle')}</h4>
                <p className="mt-1 text-sm text-white/70 text-center">{t('quickCallbackForm.formSubtitle')}</p>

                {status === 'success' ? (
                    <div className="mt-6 text-center py-4">
                        <p className="text-brand-gold-light font-semibold">{t('quickCallbackForm.successTitle')}</p>
                        <p className="mt-1 text-sm text-white/80">{t('quickCallbackForm.successMessage')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} noValidate className="mt-6 flex flex-col sm:flex-row gap-3 items-start">
                        <div className="flex-1 w-full">
                            <input
                                type="text" value={name} onChange={(e) => setName(e.target.value)}
                                placeholder={t('quickCallbackForm.namePlaceholder')}
                                aria-label={t('quickCallbackForm.nameLabel')}
                                className={inputClass('name')}
                            />
                            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>
                        <div className="flex-1 w-full">
                            <input
                                type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                                placeholder={t('quickCallbackForm.phonePlaceholder')}
                                aria-label={t('quickCallbackForm.phoneLabel')}
                                className={inputClass('phone')}
                            />
                            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>
                        <Button type="submit" variant="secondary" disabled={isSubmitting} icon={<ArrowRight size={16} />} className="w-full sm:w-auto shrink-0">
                            {isSubmitting ? t('quickCallbackForm.submittingButton') : t('quickCallbackForm.submitButton')}
                        </Button>
                    </form>
                )}
                {status === 'error' && <p className="text-red-500 text-center text-sm mt-4">{t('quickCallbackForm.errorMessage')}</p>}
            </div>
        </div>
    );
};

export default QuickCallbackForm;
